import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

/** Two panes side by side with a divider the user can drag.
 *
 *  The width is stored per browser under `storageKey`, not per practice: how much room somebody wants for a chat is a
 *  property of them and their screen, and having it reset every time they switch practice would be an odd thing for a
 *  window to do.
 *
 *  Below the breakpoint the two panes stack and the divider disappears. A drag handle needs a pointer, and a phone in
 *  portrait has no width to divide.
 */

/** Percentage of the container the right pane occupies. Clamped so neither pane can be dragged to nothing — a divider
 *  pulled to the edge looks like the panel was closed, and there is nothing on screen to open it again. */
const MIN_RIGHT_PERCENT = 22;
const MAX_RIGHT_PERCENT = 62;
const DEFAULT_RIGHT_PERCENT = 36;

const clamp = (n: number): number =>
  Math.min(MAX_RIGHT_PERCENT, Math.max(MIN_RIGHT_PERCENT, n));

const readStored = (key: string): number => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return DEFAULT_RIGHT_PERCENT;
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? clamp(parsed) : DEFAULT_RIGHT_PERCENT;
  } catch {
    // Private browsing, or storage disabled. The layout is a preference, not state the page needs.
    return DEFAULT_RIGHT_PERCENT;
  }
};

export const SplitPane = ({
  storageKey,
  left,
  right,
}: {
  storageKey: string;
  left: ReactNode;
  right: ReactNode;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [rightPercent, setRightPercent] = useState(() => readStored(storageKey));
  const [dragging, setDragging] = useState(false);
  const isWide = useIsWide();

  // Written on every move, so the width survives a reload even if the pointer never leaves the window.
  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, String(rightPercent));
    } catch {
      /* preference only */
    }
  }, [storageKey, rightPercent]);

  const applyClientX = useCallback((clientX: number) => {
    const box = containerRef.current?.getBoundingClientRect();
    if (!box || box.width === 0) return;
    setRightPercent(clamp(((box.right - clientX) / box.width) * 100));
  }, []);

  // Listeners go on the window rather than the divider: a drag that outruns the pointer — which is every drag, since
  // the divider is four pixels wide — otherwise stops the moment the cursor leaves it.
  useEffect(() => {
    if (!dragging) return;

    const onMove = (e: PointerEvent) => {
      e.preventDefault();
      applyClientX(e.clientX);
    };
    const onUp = () => setDragging(false);

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    // Without this a drag across the main pane selects its text, which then stays selected after the drop.
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = "";
    };
  }, [dragging, applyClientX]);

  const nudge = (delta: number) => setRightPercent((p) => clamp(p + delta));

  return (
    <div ref={containerRef} className="flex flex-col gap-6 lg:flex-row lg:gap-0">
      <div className="min-w-0 flex-1 lg:pr-6">{left}</div>

      {/* Hidden while stacked — there is nothing to divide. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Šířka panelu s asistentkou"
        aria-valuenow={Math.round(rightPercent)}
        aria-valuemin={MIN_RIGHT_PERCENT}
        aria-valuemax={MAX_RIGHT_PERCENT}
        tabIndex={0}
        onPointerDown={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDoubleClick={() => setRightPercent(DEFAULT_RIGHT_PERCENT)}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") {
            e.preventDefault();
            nudge(2);
          } else if (e.key === "ArrowRight") {
            e.preventDefault();
            nudge(-2);
          }
        }}
        title="Přetažením změníte šířku, dvojklikem vrátíte výchozí"
        className={
          "group hidden w-1.5 shrink-0 cursor-col-resize items-center justify-center rounded-full transition-colors lg:flex " +
          (dragging ? "bg-[#1a2b6b]" : "bg-gray-200 hover:bg-gray-300 focus:bg-[#1a2b6b] focus:outline-none")
        }
      >
        <div className="h-8 w-0.5 rounded-full bg-white/60" />
      </div>

      {/* The basis is applied only once the panes are side by side. While stacked the container is a column, where a
          flex-basis in percent would size the chat to a third of the page *height* rather than its width. */}
      <div
        className="min-w-0 lg:pl-6"
        style={isWide ? { flexBasis: `${rightPercent}%`, flexGrow: 0, flexShrink: 0 } : undefined}
      >
        {right}
      </div>
    </div>
  );
};

/** Whether the two panes are side by side. Mirrors Tailwind's `lg` breakpoint, which is where the layout switches. */
const useIsWide = (): boolean => {
  const [wide, setWide] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches,
  );

  useEffect(() => {
    const query = window.matchMedia("(min-width: 1024px)");
    const onChange = (e: MediaQueryListEvent) => setWide(e.matches);
    query.addEventListener("change", onChange);
    setWide(query.matches);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return wide;
};
